import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface DeadlineEditorProps {
  cycleId: string;
  currentDeadline?: string | null;
  getAuthHeaders: () => Promise<Record<string, string>>;
  onUpdated: () => void;
  /** Optional label override (defaults to "Review Deadline"). */
  label?: string;
  /** Compact mode: inline label, no helper paragraph (used inside other sections). */
  compact?: boolean;
  /**
   * Fires whenever the picked date changes (quick-pick click, manual edit,
   * sync from currentDeadline, save / clear). Lets callers like PreScreenView
   * grab an unsaved value and submit it together with their own action — so
   * the reviewer doesn't have to click two save buttons.
   */
  onValueChange?: (value: string) => void;
}

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:4000';

const localDateStr = (d: Date) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const deadlineToIso = (dateStr: string) =>
  new Date(`${dateStr}T23:59:59`).toISOString();

const QUICK_PICKS = [3, 7, 14, 30];

const DeadlineEditor: React.FC<DeadlineEditorProps> = ({
  cycleId, currentDeadline, getAuthHeaders, onUpdated, label = 'Review Deadline', compact = false,
  onValueChange
}) => {
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (currentDeadline) {
      setValue(localDateStr(new Date(currentDeadline)));
    } else {
      setValue('');
    }
  }, [currentDeadline]);

  // Bubble the current picked value up to the parent so callers can submit it
  // together with their own action (e.g. PreScreenView's "Send to Review").
  useEffect(() => {
    onValueChange?.(value);
  }, [value, onValueChange]);

  const setDeadlineInDays = (n: number) => {
    const d = new Date();
    d.setDate(d.getDate() + n);
    setValue(localDateStr(d));
    setMsg(null);
  };

  const handleSave = async () => {
    setMsg(null);
    if (!value) {
      setMsg('Pick a date first');
      return;
    }
    const ts = new Date(`${value}T23:59:59`).getTime();
    if (Number.isNaN(ts) || ts <= Date.now()) {
      setMsg('Deadline must be in the future');
      return;
    }
    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      await axios.patch(`${backendUrl}/api/content-review/cycles/${cycleId}/deadline`, {
        reviewDeadline: deadlineToIso(value)
      }, { headers });
      setMsg('Saved');
      onUpdated();
    } catch (err: any) {
      setMsg(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleClear = async () => {
    setMsg(null);
    try {
      setSaving(true);
      const headers = await getAuthHeaders();
      await axios.patch(`${backendUrl}/api/content-review/cycles/${cycleId}/deadline`, {
        reviewDeadline: null
      }, { headers });
      setValue('');
      setMsg('Cleared');
      onUpdated();
    } catch (err: any) {
      setMsg(err.response?.data?.error || 'Failed to clear');
    } finally {
      setSaving(false);
    }
  };

  const msgIsError = msg && msg !== 'Saved' && msg !== 'Cleared';

  return (
    <div className={compact ? 'deadline-editor' : 'review-form-group deadline-editor'}>
      {!compact && <label style={{ marginBottom: 6, display: 'block' }}>{label}</label>}
      <div className="deadline-editor-row">
        {compact && <span className="deadline-editor-label">{label}:</span>}
        {QUICK_PICKS.map(n => (
          <button
            key={n}
            type="button"
            onClick={() => setDeadlineInDays(n)}
            className="deadline-quick-chip"
          >
            {n} days
          </button>
        ))}
        <input
          type="date"
          className="deadline-date-input"
          aria-label="Review deadline date"
          value={value}
          onChange={e => { setValue(e.target.value); setMsg(null); }}
        />
        <button
          type="button"
          onClick={handleSave}
          disabled={saving || !value}
          className="deadline-action-btn"
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        {currentDeadline && (
          <button
            type="button"
            onClick={handleClear}
            disabled={saving}
            className="deadline-action-btn deadline-clear-btn"
          >
            Clear
          </button>
        )}
        {msg && (
          <span className={`deadline-msg ${msgIsError ? 'deadline-msg-error' : 'deadline-msg-success'}`}>
            {msg}
          </span>
        )}
      </div>
      {!compact && (
        <p className="deadline-helper-text">
          Optional. Saving here is independent of "Send to Review". Reviewers get a 24h reminder and a missed-deadline escalation.
        </p>
      )}
    </div>
  );
};

export default DeadlineEditor;
